import React, { useState } from "react";
import { Container, InputLabel, Tooltip, Box, Paper, Grid, Modal, Typography, TextField, Button, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";


const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const addSubjectInfoSections = [
  {
    title: "Subject Name & Short Name",
    color: "#1976D2",
    description: "Enter the full name of the subject and its abbreviated short name used across reports and timetables.",
    fields: [
      "Subject Name – Full name of the subject (required)",
      "Short Name – Abbreviated label for the subject (required)",
    ],
  },
  {
    title: "Subject Group",
    color: "#7B1FA2",
    description: "Select the group/category this subject belongs to.",
    fields: [
      "Subject – Standard academic subject",
      "Practical – Lab or hands-on subject",
      "Oral – Spoken/verbal assessment subject",
    ],
  },
  {
    title: "Subject Type",
    color: "#388E3C",
    description: "Define whether this subject is mandatory, optional, or an additional offering for students.",
    fields: [
      "Compulsory – All students must take this subject",
      "Optional – Students may choose this subject",
      "Additional – Extra subject beyond the standard curriculum",
    ],
  },
  {
    title: "Time Table Priority",
    color: "#F57C00",
    description: "Set the scheduling priority for this subject when generating timetables.",
    fields: [
      "High – Scheduled first during timetable generation",
      "Normal – Standard scheduling priority",
      "Low – Scheduled last during timetable generation",
    ],
  },
  {
    title: "Image Upload",
    color: "#0277BD",
    description: "Optionally upload an image or icon to represent this subject visually.",
    fields: [
      "Select an image file using the file input",
      "Click 'Upload Image' to upload and get the image path",
      "Uploaded image path is included in the save payload",
    ],
  },
  {
    title: "Actions",
    color: "#C62828",
    description: "Use the action buttons to submit the new subject or reset the form.",
    fields: [
      "SAVE – Submits the subject data via POST request (disabled until all required fields are filled)",
      "RESET – Clears all form fields and resets to default values",
      "Required fields: Subject Name, Short Name, Subject Type, Subject Group, Priority",
    ],
  },
];

function AddSubjectMaster() {
  let navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // ── Info dialog state ──
  const [infoOpen, setInfoOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
const [modalMessage, setModalMessage] = useState('');
const [modalType, setModalType] = useState('success'); // 'success' or 'error'

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [formData, setFormData] = useState({
    subjectName: '',
    shortName: '',
    subjectGroupID: 0,
    subjectType: 0,
    gradeSystem: '',
    priority: '',
    imageURL: ''
  });


  const handleCancel = () => {
    setFormData({
      subjectName: '',
      shortName: '',
      subjectGroupID: 0,
      subjectType: 0,
      gradeSystem: '',
      priority: '',
      imageURL: ''
    });
    setSelectedImage(null);
    setImagePath('');
  };

const handleSave = async () => {
  try {
    const url = process.env.REACT_APP_BASE_URL;
    const apiUrl = `${url}/SubjectMaster`;
    const token = sessionStorage.getItem('token');

    const payload = {
      subjectName: formData.subjectName,
      shortName: formData.shortName,
      subjectGroupID: formData.subjectGroupID,
      subjectType: formData.subjectType,
      gradeSystem: formData.gradeSystem,
      priority: formData.priority,
      imageURL: imagePath,
    };

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

// Check the status field in the response body FIRST, regardless of HTTP status
if (responseData.status === "Failed!") {
  setModalType('error');
  setModalMessage(responseData.msg || 'Unable to add Subject');
  setModalOpen(true);
} else if (response.ok) {
  setModalType('success');
  setModalMessage(responseData.msg || 'Subject Added Successfully');
  setModalOpen(true);
  // Reset form and image
  setFormData({
    subjectName: '',
    shortName: '',
    subjectGroupID: 0,
    subjectType: 0,
    gradeSystem: '',
    priority: '',
    imageURL: ''
  });
  setSelectedImage(null);
  setImagePath('');
} else {
  setModalType('error');
  setModalMessage(responseData.msg || 'Unable to add Subject');
  setModalOpen(true);
}
  } catch (error) {
    console.error('API request error:', error);
    setModalType('error');
    setModalMessage('An error occurred. Please try again later.');
    setModalOpen(true);
  }
};


  const handleImageSelect = (e) => {
    setSelectedImage(e.target.files[0]);
  };

  const handleImageUpload = async () => {
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);

      const token = sessionStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch('${apiUrl}/Employee/EmployeeImageUpload', {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        setImagePath(responseData.filePath);
        // alert('Image uploaded successfully.');
        setSnackbarMessage('Image uploaded successfully.');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        // alert('Failed to upload Image.');
        setSnackbarMessage('Failed to upload Image.');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      // alert('An error occurred while uploading image. Please try again later.');
      setSnackbarMessage('An error occurred while uploading image. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '12px auto',
          width: "100%",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
gap: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Add Subject
        </Typography>

        {/* ── Info Button ── */}
        <IconButton
          onClick={() => setInfoOpen(true)}
          title="Form Information"
          style={{
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "2px solid rgba(255,255,255,0.6)",
            borderRadius: "50%",
            padding: "6px",
            color: "#fff",
          }}
        >
          <InfoIcon style={{ fontSize: 26 }} />
        </IconButton>
      </Box>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Add Subject — Form Guide
            </Typography>
          </Box>
          
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This form is used to create a new subject record. Fill in all required fields and click SAVE to submit. Use RESET to clear the form at any time.
          </Typography>

          {addSubjectInfoSections.map((section, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${section.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
              >
                {section.title}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {section.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                📝 DETAILS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {section.fields.map((field, i) => (
                  <Chip
                    key={i}
                    label={field}
                    size="small"
                    variant="outlined"
                    style={{
                      borderColor: section.color,
                      color: section.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} style={{ padding: 20 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Subject Name" fullWidth value={formData.subjectName} onChange={(e) => setFormData({ ...formData, subjectName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Short Name" fullWidth value={formData.shortName} onChange={(e) => setFormData({ ...formData, shortName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <FormControl fullWidth>
              <FormLabel>Subject Group</FormLabel>
              <Select value={formData.subjectGroupID} onChange={(e) => setFormData({ ...formData, subjectGroupID: e.target.value })} fullWidth>
                <MenuItem value="">--Select--</MenuItem>
                <MenuItem value="1">Subject</MenuItem>
                <MenuItem value="2">Practical</MenuItem>
                <MenuItem value="3">Oral</MenuItem>
              </Select>
            </FormControl> */}
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <FormControl fullWidth>
              <FormLabel>Subject Type</FormLabel>
              <RadioGroup row value={formData.subjectType} onChange={(e) => setFormData({ ...formData, subjectType: e.target.value })}>
                <FormControlLabel value="1" control={<Radio />} label="Compulsory" />
                <FormControlLabel value="2" control={<Radio />} label="Optional" />
                <FormControlLabel value="3" control={<Radio />} label="Additional" />
              </RadioGroup>
            </FormControl> */}
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Subject's Grading System</FormLabel>
              <RadioGroup row value={formData.gradeSystem} onChange={(e) => setFormData({ ...formData, gradeSystem: e.target.value })}>
                <FormControlLabel value="Nine" control={<Radio />} label="Nine" />
                <FormControlLabel value="Six" control={<Radio />} label="Six" />
                <FormControlLabel value="Three" control={<Radio />} label="Three" />
              </RadioGroup>
            </FormControl>
          </Grid> */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Time Table Priority</FormLabel>
              <RadioGroup row value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                <FormControlLabel value="High" control={<Radio />} label="High" />
                <FormControlLabel value="Normal" control={<Radio />} label="Normal" />
                <FormControlLabel value="Low" control={<Radio />} label="Low" />
              </RadioGroup>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <InputLabel htmlFor="resume">Image</InputLabel>
            <input
              type="file"
              className="form-control"
              id="resume"
              onChange={handleImageSelect}
            />
            <Button variant="contained" component="span" onClick={handleImageUpload}>
              Upload Image
            </Button>
          </Grid> */}
          <Grid item xs={12}>
            <Box display="flex" >
              <Tooltip title={(!formData.subjectName || !formData.shortName || !formData.subjectType || !formData.subjectGroupID || !formData.priority) && "Please fill all fields"} arrow><span>
                <Button variant="contained" color="primary" onClick={handleSave} style={{
                  width: "100px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: 'bold'
                }}
                  disabled={!formData.subjectName || !formData.shortName || !formData.priority}
                >
                  SAVE
                </Button></span></Tooltip>
              <Button variant="contained" color="error" onClick={handleCancel} style={{
                marginLeft: 10, width: "100px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}>
                RESET
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

  <Modal
  open={modalOpen}
  onClose={() => setModalOpen(false)}
  aria-labelledby="response-modal-title"
  aria-describedby="response-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Dynamic Header: Green for success, Red for error */}
    <Box sx={{
      backgroundColor: modalType === 'success' ? '#0B1F40' : '#f44336', // Green or Red
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    }}>
      <Typography id="response-modal-title" variant="h6" sx={{ color: '#fff' }}>
        {modalType === 'success' ? 'Success' : 'Error'}
      </Typography>
      {modalType === 'success' ? (
        <CheckCircleOutlineIcon sx={{ color: '#fff' }} />
      ) : (
        <ErrorOutlineIcon sx={{ color: '#fff' }} />
      )}
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="response-modal-description" sx={{ mb: 2 }}>
        {modalMessage}
      </Typography>

      {/* Dynamic Button: Green for success, Red for error */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
        onClick={() => {
  setModalOpen(false);
  if (modalType === 'success') {
    navigate(-1);
  }
}}
          variant="contained"
          color={modalType === 'success' ? 'primary' : 'error'}
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AddSubjectMaster;